import { Box, IconButton, MenuItem, Select, SelectChangeEvent, Typography } from '@mui/material';
import {
  gridFilteredSortedRowEntriesSelector,
  gridPageCountSelector,
  gridPaginationModelSelector,
  gridPaginationRowCountSelector,
  useGridApiContext,
  useGridRootProps,
  useGridSelector,
} from '@mui/x-data-grid-pro';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { fontSize } from '@verantos-dev/shared-ui-components/tokens';

import Icon from '../../../Icon/Icon';
import { TextField } from '../../../TextField/TextField';

export const DataGridPagination = () => {
  const intl = useIntl();

  /** DataGrid Context */
  const apiRef = useGridApiContext();
  const { estimatedRowCount, rows, paginationMode, pageSizeOptions, onPaginationModelChange } =
    useGridRootProps();
  const { pageSize, page } = useGridSelector(apiRef, gridPaginationModelSelector);
  const pageCount = useGridSelector(apiRef, gridPageCountSelector);
  const rowCount = useGridSelector(apiRef, gridPaginationRowCountSelector);

  // In client mode, get filtered row count to correctly calculate pagination when filters are applied
  // Always call the hook (React Hooks rules), but only use the result in client mode
  const visibleRowEntries = useGridSelector(apiRef, gridFilteredSortedRowEntriesSelector);
  const visibleRowCount = paginationMode === 'client' ? visibleRowEntries.length : null;

  // Calculate total page count based on filtered rows in client mode, or use pageCount in server mode
  const totalPageCount =
    paginationMode === 'server'
      ? pageCount
      : visibleRowCount !== null
      ? Math.ceil(visibleRowCount / pageSize)
      : Math.ceil(rows.length / pageSize);

  // Total row count: in server mode use server-provided count, in client mode use total unfiltered count
  // (for display as "of Z rows" - we want to show total available, not just filtered)
  const totalRowCount =
    paginationMode === 'server'
      ? rowCount || estimatedRowCount || -1 // Use server-provided row count
      : rows.length; // Use client-side total unfiltered row count

  /** Internal state */
  // const searchParamPage = urlParams.get('page') || '';
  const [goToPage, setGoToPage] = useState<string>((page + 1).toString());

  // sync goToPage with page
  useEffect(() => {
    setGoToPage((page + 1).toString());
  }, [page]);

  const handlePageSizeChange = (event: SelectChangeEvent<string | number>) => {
    const newPageSize = parseInt(event.target.value as string, 10);

    // Reset goToPage to 1
    setGoToPage('1');

    // update grid pagination model
    if (onPaginationModelChange) {
      onPaginationModelChange(
        { page: 0, pageSize: newPageSize },
        {
          api: apiRef.current,
          reason: 'setPaginationModel',
        }
      );
    } else {
      apiRef.current.setPageSize(newPageSize);
    }
  };

  const onPageChange = (newPage: number) => {
    // Reset goToPage to new page
    setGoToPage((newPage + 1).toString());

    // update grid pagination model
    if (onPaginationModelChange) {
      onPaginationModelChange(
        { page: newPage, pageSize },
        {
          api: apiRef.current,
          reason: 'setPaginationModel',
        }
      );
    } else {
      apiRef.current.setPage(newPage);
    }
  };

  const onGoToPageChange = (id: string, value: string) => {
    // Handle empty string
    if (value === '') {
      setGoToPage(value);
      return;
    }
    if (/^\d*$/.test(value)) {
      const numberValue = parseInt(value, 10);
      setGoToPage(numberValue.toString());
    }
  };

  const onEnterPageBlur = () => {
    if (goToPage === '') {
      setGoToPage((page + 1).toString());
    } else {
      const numberValue = parseInt(goToPage, 10) - 1;
      if (numberValue >= 0 && numberValue < totalPageCount) {
        onPageChange(numberValue);
      } else {
        setGoToPage((page + 1).toString());
      }
    }
  };

  const onGoToPageKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      onEnterPageBlur();
    }
  };

  // Calculate start/end items based on filtered rows in client mode, or total rows in server mode
  // In client mode with filters, we want to show "X to Y of Z rows" where X-Y is within filtered results
  const effectiveRowCount =
    paginationMode === 'client' && visibleRowCount !== null ? visibleRowCount : totalRowCount;
  const startItem = Math.min(page * pageSize + 1, effectiveRowCount);
  const endItem = Math.min((page + 1) * pageSize, effectiveRowCount);
  const goToWidth =
    Math.max(Math.max(totalPageCount.toString().length, goToPage.length) * 8, 8) + 14;

  return (
    <Box display="flex" justifyContent="space-between" alignItems="center" p={4}>
      {/* rows per page */}
      <Box display="flex" alignItems="center">
        <Typography variant="body2" sx={{ fontSize: fontSize[12] }}>
          {intl.formatMessage({ id: 'dataGrid.pagination.rows.perPage' })}
        </Typography>
        <Select
          value={pageSize}
          onChange={handlePageSizeChange}
          disabled={
            pageSizeOptions?.length <= 1 || paginationMode === 'server'
              ? !rowCount || rowCount < 0
              : totalRowCount <= 0
          }
          sx={{
            // ml: 1,
            fontSize: fontSize[12],
            '& .MuiSelect-select': {
              padding: '6px 16px 6px 8px',
              backgroundColor: 'transparent',
              border: 'none',
              fontSize: fontSize[12],
              '&:focus': {
                backgroundColor: 'transparent',
              },
            },
            '& .MuiOutlinedInput-notchedOutline': {
              border: 'none',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              border: 'none',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              border: 'none',
            },
            '& .MuiSelect-icon': {
              color: 'inherit',
            },
          }}
        >
          {pageSizeOptions?.map((option) => (
            <MenuItem
              key={typeof option === 'number' ? option : option.value}
              value={typeof option === 'number' ? option : option.value}
            >
              {typeof option === 'number' ? option : option.label}
            </MenuItem>
          ))}
        </Select>
      </Box>

      {/* current location in the list */}
      <Box mr={6} ml={4}>
        <Typography variant="body2" sx={{ fontSize: fontSize[12] }}>
          {intl.formatMessage(
            { id: 'dataGrid.pagination.items.current' },
            {
              startItem,
              endItem,
              totalItems: intl.formatNumber(totalRowCount),
            }
          )}
        </Typography>
      </Box>

      {/* separator */}
      <Box
        sx={(theme) => ({
          backgroundColor: theme.palette.divider,
          width: '1px',
          height: theme.spacing(10),
        })}
      ></Box>

      {/* go to page */}
      <Box ml={4} mr={6} sx={{ display: 'flex', alignItems: 'center' }}>
        <Typography variant="body2" sx={{ mr: 2, fontSize: fontSize[12] }}>
          {intl.formatMessage(
            { id: 'dataGrid.pagination.goToPage.page' },
            { totalPages: totalPageCount }
          )}
        </Typography>
        <TextField
          id="pagination-goto-page"
          width={goToWidth}
          value={goToPage.toString()}
          displayLabel={''}
          onChange={onGoToPageChange}
          onBlur={onEnterPageBlur}
          onKeyDown={onGoToPageKeyPress}
          disabled={
            pageSizeOptions?.length <= 1 || paginationMode === 'server'
              ? !rowCount || rowCount < 0
              : totalRowCount <= 0
          }
          error={
            goToPage !== '' &&
            (parseInt(goToPage, 10) < 1 || parseInt(goToPage, 10) > totalPageCount)
          }
          sx={{
            '& .MuiInputBase-input': {
              fontSize: fontSize[12],
              padding: '8px',
              // Remove spinner (incrementor)
              '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
                display: 'none',
                margin: 0,
              },
              mozAppearance: 'textfield', // Firefox
            },
          }}
        />
        <Typography variant="body2" sx={{ ml: 2, fontSize: fontSize[12] }}>
          {intl.formatMessage(
            { id: 'dataGrid.pagination.goToPage.current' },
            { totalPages: totalPageCount }
          )}
        </Typography>
      </Box>

      {/* pagination buttons */}
      <Box display="flex" alignItems="center">
        {/* go to start of list */}
        <IconButton
          onClick={(event) => onPageChange(0)}
          disabled={page === 0}
          sx={{ height: '24px', width: '24px', p: 0, fillOpacity: page === 0 ? 0.3 : 0.54 }}
        >
          <Icon
            id="previousPage"
            icon="startPagination"
            titleAccess={intl.formatMessage({ id: 'dataGrid.pagination.icon.startOfList' })}
          />
        </IconButton>
        {/* go to previous page */}
        <IconButton
          onClick={(event) => onPageChange(page - 1)}
          disabled={page === 0}
          sx={{ height: '24px', width: '24px', p: 0, fillOpacity: page === 0 ? 0.3 : 0.54 }}
        >
          <Icon
            id="previousPage"
            icon="previousPagination"
            titleAccess={intl.formatMessage({ id: 'dataGrid.pagination.icon.previousPage' })}
          />
        </IconButton>
        {/* go to next page */}
        <IconButton
          onClick={(event) => onPageChange(page + 1)}
          disabled={page >= totalPageCount - 1}
          sx={{
            height: '24px',
            width: '24px',
            p: 0,
            fillOpacity: page >= totalPageCount - 1 ? 0.3 : 0.54,
          }}
        >
          <Icon
            id="nextPage"
            icon="nextPagination"
            titleAccess={intl.formatMessage({ id: 'dataGrid.pagination.icon.nextPage' })}
          />
        </IconButton>
        {/*  go to end of list */}
        <IconButton
          onClick={(event) => onPageChange(totalPageCount - 1)}
          disabled={page >= totalPageCount - 1}
          sx={{
            height: '24px',
            width: '24px',
            p: 0,
            fillOpacity: page === totalPageCount - 1 ? 0.3 : 0.54,
          }}
        >
          <Icon
            id="nextPage"
            icon="endPagination"
            titleAccess={intl.formatMessage({ id: 'dataGrid.pagination.icon.endOfList' })}
          />
        </IconButton>
      </Box>
    </Box>
  );
};
